import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { usePublicSiteBundle } from '@/hooks/usePublicSiteBundle'
import { readTrackingFromIntegrations } from '@/lib/siteTracking'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
    ttq?: {
      page: () => void
      track: (e: string, p?: object) => void
      load: (id: string, o?: object) => void
      push?: (args: unknown[]) => void
    }
    TiktokAnalyticsObject?: string
    snaptr?: (...args: unknown[]) => void
  }
}

function loadGoogleGtag(gaId: string, awId: string | undefined) {
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`
  document.head.appendChild(s)
  window.gtag('js', new Date())
  window.gtag('config', gaId, { send_page_view: false })
  if (awId) {
    window.gtag('config', awId, { send_page_view: false })
  }
}

/** Meta Pixel base snippet + init */
function loadFacebookPixel(pixelId: string) {
  if (window.fbq) {
    window.fbq('init', pixelId)
    return
  }
  const f = window
  const b = document
  const e = 'script'
  const n = (f.fbq = function (...args: unknown[]) {
    const fbq = n as unknown as { callMethod?: (...a: unknown[]) => void; queue: unknown[]; apply?: unknown }
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue.push(args)
    }
  }) as unknown as { queue: unknown[]; loaded?: boolean; version?: string }
  if (!f._fbq) f._fbq = n
  n.queue = []
  n.loaded = true
  n.version = '2.0'
  const t = b.createElement(e)
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const s = b.getElementsByTagName(e)[0]
  s?.parentNode?.insertBefore(t, s)
  const fb = window.fbq as ((...a: unknown[]) => void) | undefined
  fb?.('init', pixelId)
  fb?.('track', 'PageView')
}

/** TikTok Pixel — load official `events.js` (sdkid + lib) then `ttq.load` + `ttq.page`. */
function loadTikTokPixel(pixelId: string) {
  if (document.querySelector(`script[data-ttq-pixel="${pixelId}"]`)) {
    window.ttq?.load?.(pixelId)
    window.ttq?.page?.()
    return
  }
  const s = document.createElement('script')
  s.async = true
  s.setAttribute('data-ttq-pixel', pixelId)
  s.src = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`
  s.onload = () => {
    try {
      window.ttq?.load?.(pixelId)
      window.ttq?.page?.()
    } catch {
      /* ignore */
    }
  }
  document.head.appendChild(s)
}

function loadSnapchatPixel(pixelId: string) {
  if (window.snaptr) {
    window.snaptr('init', pixelId, {})
    window.snaptr('track', 'PAGE_VIEW')
    return
  }
  const e = window
  const t = document
  const n = 'script'
  const a = (e.snaptr = function (...args: unknown[]) {
    const sn = a as unknown as { handleRequest?: (...x: unknown[]) => void; queue: unknown[] }
    if (sn.handleRequest) sn.handleRequest(...args)
    else sn.queue.push(args)
  }) as unknown as { queue: unknown[] }
  a.queue = []
  const r = t.createElement(n)
  r.async = true
  r.src = 'https://sc-static.net/scevent.min.js'
  const u = t.getElementsByTagName(n)[0]
  u?.parentNode?.insertBefore(r, u)
  r.addEventListener('load', () => {
    window.snaptr?.('init', pixelId, {})
    window.snaptr?.('track', 'PAGE_VIEW')
  })
}

export function PublicTrackingPixels() {
  const location = useLocation()
  const bundle = usePublicSiteBundle()
  const tracking = readTrackingFromIntegrations(bundle.data?.site_config?.integrations)

  const gaId =
    tracking.google_analytics_measurement_id ||
    (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ||
    ''
  const awId = tracking.google_ads_conversion_id?.trim() || undefined
  const fbId = tracking.facebook_pixel_id?.trim() || ''
  const ttId = tracking.tiktok_pixel_id?.trim() || ''
  const snapId = tracking.snapchat_pixel_id?.trim() || ''

  const gaLoaded = useRef(false)
  const fbLoaded = useRef(false)
  const ttLoaded = useRef(false)
  const snapLoaded = useRef(false)

  useEffect(() => {
    if (!gaId || gaLoaded.current) return
    gaLoaded.current = true
    loadGoogleGtag(gaId, awId)
  }, [gaId, awId])

  useEffect(() => {
    if (!gaId || typeof window.gtag !== 'function') return
    const path = location.pathname + location.search
    window.gtag('config', gaId, { page_path: path })
    if (awId) window.gtag('config', awId, { page_path: path })
  }, [gaId, awId, location.pathname, location.search])

  useEffect(() => {
    if (!fbId || fbLoaded.current) return
    fbLoaded.current = true
    loadFacebookPixel(fbId)
  }, [fbId])

  useEffect(() => {
    if (!fbId) return
    const fb = window.fbq as ((...a: unknown[]) => void) | undefined
    if (typeof fb !== 'function') return
    fb('track', 'PageView')
  }, [fbId, location.pathname, location.search])

  useEffect(() => {
    if (!ttId || ttLoaded.current) return
    ttLoaded.current = true
    loadTikTokPixel(ttId)
  }, [ttId])

  useEffect(() => {
    if (!ttId || !window.ttq?.page) return
    window.ttq.page()
  }, [ttId, location.pathname, location.search])

  useEffect(() => {
    if (!snapId || snapLoaded.current) return
    snapLoaded.current = true
    loadSnapchatPixel(snapId)
  }, [snapId])

  useEffect(() => {
    if (!snapId || typeof window.snaptr !== 'function') return
    window.snaptr('track', 'PAGE_VIEW')
  }, [snapId, location.pathname, location.search])

  return null
}
